import { Play, Pause } from "lucide-react";
import { Button } from "./button";
import { cn } from "../lib/utils";
import { forwardRef, useEffect, useRef, useState } from "react";

export interface AudioPlayerProps extends Omit<React.HTMLAttributes<HTMLDivElement>, "onError"> {
  /**
   * Audio file source URL (supports MP3, WAV, OGG formats)
   */
  src: string;
  /**
   * A total duration in seconds to override the duration displayed based on the file loaded
   */
  durationOverride?: number;
  /**
   * Callback fired when playback starts
   */
  onPlay?: () => void;
  /**
   * Callback fired when playback is paused
   */
  onPause?: () => void;
  /**
   * Callback fired when playback ends
   */
  onEnded?: () => void;
  /**
   * Callback fired when an error occurs loading or playing the audio
   */
  onAudioError?: (error: string) => void;
}

/**
 * AudioPlayer component with play/pause controls, seekable progress bar, and time display.
 * Supports MP3, WAV, and OGG audio formats.
 */
export const AudioPlayer = forwardRef<HTMLDivElement, AudioPlayerProps>((props, ref) => (
  <AudioPlayerInternal {...props} ref={ref} key={props.src} />
));

const AudioPlayerInternal = forwardRef<HTMLDivElement, AudioPlayerProps>(
  ({ src, className, durationOverride, onPlay, onPause, onEnded, onAudioError, ...props }, ref) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const progressBarRef = useRef<HTMLDivElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(durationOverride ?? 0);
    const [isLoading, setIsLoading] = useState(true);

    // Format time as MM:SS
    const formatTime = (timeInSeconds: number): string => {
      if (isNaN(timeInSeconds) || !isFinite(timeInSeconds)) {
        return "0:00";
      }
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = Math.floor(timeInSeconds % 60);
      return `${minutes}:${seconds.toString().padStart(2, "0")}`;
    };

    // Handle play/pause toggle
    const togglePlayPause = () => {
      if (!audioRef.current) return;

      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch((error) => {
          console.error("Error playing audio:", error);
          onAudioError?.("Failed to play audio");
        });
      }
    };

    // Handle seeking by clicking on progress bar
    const handleProgressBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
      if (!audioRef.current || !progressBarRef.current || isLoading || !src) return;

      const rect = progressBarRef.current.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const percentage = clickX / rect.width;
      const newTime = percentage * duration;

      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    };

    // Set up audio element event listeners syncing with browser's audio element
    useEffect(() => {
      const audio = audioRef.current;
      if (!audio) return;

      const handleLoadedMetadata = () => {
        if (durationOverride === undefined) setDuration(audio.duration);
        setIsLoading(false);
      };

      const handleTimeUpdate = () => {
        setCurrentTime(audio.currentTime);
      };

      const handlePlay = () => {
        setIsPlaying(true);
        onPlay?.();
      };

      const handlePause = () => {
        setIsPlaying(false);
        onPause?.();
      };

      const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
        onEnded?.();
      };

      const handleError = () => {
        setIsLoading(false);
        onAudioError?.("Failed to load audio file");
      };

      audio.addEventListener("loadedmetadata", handleLoadedMetadata);
      audio.addEventListener("timeupdate", handleTimeUpdate);
      audio.addEventListener("play", handlePlay);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("error", handleError);

      return () => {
        audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
        audio.removeEventListener("timeupdate", handleTimeUpdate);
        audio.removeEventListener("play", handlePlay);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("error", handleError);
      };
    }, [onPlay, onPause, onEnded, onAudioError, durationOverride]);

    // Calculate progress percentage
    const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

    return (
      <div ref={ref} className={cn("flex items-center gap-3", className)} {...props}>
        {/* Hidden audio element with key prop to reset state on src change */}
        <audio ref={audioRef} src={src} preload="metadata" />

        {/* Play/Pause Button */}
        <Button
          size="sm"
          variant="outline"
          onClick={togglePlayPause}
          disabled={isLoading || !src}
          aria-label={isPlaying ? "Pause" : "Play"}
        >
          {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
        </Button>

        {/* Clickable Progress Bar */}
        <div
          ref={progressBarRef}
          className="flex-1 bg-gray-200 rounded h-2 relative cursor-pointer"
          onClick={handleProgressBarClick}
          role="progressbar"
          aria-label="Seek audio"
          aria-valuenow={currentTime}
          aria-valuemin={0}
          aria-valuemax={duration}
        >
          <div
            className="bg-blue-500 h-2 rounded transition-all duration-100"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        {/* Time Display */}
        <span className="text-xs text-gray-500 tabular-nums">
          {formatTime(currentTime)} / {formatTime(duration)}
        </span>
      </div>
    );
  }
);

AudioPlayer.displayName = "AudioPlayer";
